import Phaser from "phaser";
import { CARAVAN_ART } from "../config/caravanArt";
import { caravanMotion, type CaravanState, type CaravanTier } from "../domain/treasuryCaravan";
export type TreasuryRuntime = {
  accent(mode: "idle" | "waiting" | "success"): void;
  setCaravan(tier: CaravanTier, state: CaravanState): void;
  pause(paused: boolean): void; destroy(): void;
};
/** Isolated decorative scene; never determines payment or route progress. */
export function createTreasuryGame(parent: HTMLElement, prefersReduced: boolean, compact = false): TreasuryRuntime {
  const media = window.matchMedia("(prefers-reduced-motion: reduce)");
  let reduced = prefersReduced || media.matches, paused = false, visible = true;
  let tier: CaravanTier = 0, state: CaravanState = "ready", burst = -Infinity;
  const ratio = Math.min(window.devicePixelRatio || 1, 2);
  class CaravanScene extends Phaser.Scene {
    body?: Phaser.GameObjects.Image;
    wheels: Phaser.GameObjects.Image[] = [];
    shadow?: Phaser.GameObjects.Ellipse;
    motes?: Phaser.GameObjects.Graphics;
    wagonW = 0; ground = 0; center = 0;
    preload() {
      CARAVAN_ART.tiers.forEach((art, i) => this.load.image(`caravan-${i}`, art.body));
      this.load.image("caravan-wheel", CARAVAN_ART.wheel);
    }
    create() {
      if (!CARAVAN_ART.tiers.every((_, i) => this.textures.exists(`caravan-${i}`)) || !this.textures.exists("caravan-wheel")) return;
      this.shadow = this.add.ellipse(0, 0, 1, 1, 0x153e37, .23);
      this.motes = this.add.graphics();
      this.body = this.add.image(0, 0, `caravan-${tier}`).setOrigin(0, 0);
      this.wheels = [0, 1].map(() => this.add.image(0, 0, "caravan-wheel"));
      this.layout(); this.scale.on("resize", () => this.layout());
      this.game.events.once(Phaser.Core.Events.POST_RENDER, () => { parent.dataset.ready = "true"; });
    }
    layout() {
      const w = this.scale.width, h = this.scale.height;
      const title = parent.parentElement?.querySelector(".treasury-heading");
      const top = compact ? 12 * ratio : title ? (title.getBoundingClientRect().bottom - parent.getBoundingClientRect().top + 16) * ratio : h * .34;
      this.ground = compact ? h * .83 : h - 84 * ratio;
      this.wagonW = Math.max(1, Math.min(w * (compact ? .46 : .56), (this.ground - top) / .93));
      this.center = w * .51;
      this.shadow?.setPosition(this.center, this.ground + 2 * ratio).setDisplaySize(this.wagonW * .95, 9 * ratio);
      this.body?.setTexture(`caravan-${tier}`);
      const art = CARAVAN_ART.tiers[tier];
      this.body?.setDisplaySize(this.wagonW, this.wagonW * art.ratio);
      this.wheels.forEach(wheel => wheel.setDisplaySize(this.wagonW * art.wheel, this.wagonW * art.wheel));
      parent.dataset.caravanTier = String(tier); parent.dataset.caravanState = state;
      parent.style.setProperty("--caravan-width", `${this.wagonW / ratio}px`);
      parent.style.setProperty("--caravan-ground", `${this.ground / ratio}px`);
      parent.dataset.relicTop = String((this.ground - this.wagonW * .93) / ratio);
    }
    override update(time: number) {
      if (!this.body || !this.motes) return;
      const moving = state === "travelling" || state === "preview";
      const m = caravanMotion(time, moving, reduced);
      const art = CARAVAN_ART.tiers[tier], wheel = this.wagonW * art.wheel;
      const x = this.center - this.wagonW / 2, axleY = this.ground - wheel / 2;
      this.body.setPosition(x, axleY - this.wagonW * art.ratio * art.axleY + m.bob * ratio);
      this.wheels.forEach((w, i) => w.setPosition(x + this.wagonW * art.axles[i === 0 ? 0 : 1], axleY).setRotation(m.wheel));
      this.motes.clear();
      if (reduced) return;
      if (moving) for (let i = 0; i < 12; i++) {
        const w = this.scale.width, span = w + 50 * ratio;
        const xx = ((i * w / 9 - m.travel * ratio) % span + span) % span - 25 * ratio;
        this.motes.lineStyle(ratio, 0xc6a465, .26).lineBetween(xx, this.ground + 10 * ratio, xx + 15 * ratio, this.ground + 10 * ratio);
        const age = (time / 1200 + i / 12) % 1;
        this.motes.fillStyle(0xffdda1, (1 - age) * .22).fillCircle(x - age * 40 * ratio, this.ground - age * 15 * ratio, (1 + age * 3) * ratio);
      }
      for (let i = 0; i < 7; i++) {
        const glow = (Math.sin(time / 1000 + i * 2.3) + 1) * .2;
        this.motes.fillStyle(0xfff5c7, glow).fillEllipse(this.scale.width * ((i * .173 + .1) % 1), this.ground - (22 + i % 3 * 9) * ratio, 7 * ratio, ratio);
      }
      const age = (time - burst) / 1000;
      if (age >= 0 && age < 1) for (let i = 0; i < 10; i++) {
        this.motes.fillStyle(0xffe4a5, (1 - age) * .7).fillCircle(this.center + Math.cos(i * .628) * age * this.wagonW * .6, axleY - this.wagonW * .3 + Math.sin(i * .628) * age * this.wagonW * .4, 2 * ratio);
      }
    }
  }
  const canvas = document.createElement("canvas"); canvas.setAttribute("aria-hidden", "true");
  const scene = new CaravanScene();
  const game = new Phaser.Game({ type: Phaser.CANVAS, parent, canvas, transparent: true, banner: false,
    width: Math.max(1, parent.clientWidth * ratio), height: Math.max(1, parent.clientHeight * ratio),
    audio: { noAudio: true }, input: { activePointers: 0 }, fps: { target: reduced ? 10 : 60 }, scene: [scene] });
  const resize = new ResizeObserver(() => game.scale.resize(Math.max(1, parent.clientWidth * ratio), Math.max(1, parent.clientHeight * ratio)));
  resize.observe(parent);
  const wake = () => { if (document.hidden || !visible) game.loop.sleep(); else game.loop.wake(); };
  const observer = new IntersectionObserver(entries => { visible = entries[0]?.isIntersecting ?? true; wake(); }); observer.observe(parent);
  const reduce = () => { reduced = paused || prefersReduced || media.matches; };
  media.addEventListener("change", reduce); document.addEventListener("visibilitychange", wake);
  return {
    accent(next) { if (next === "success") burst = scene.time.now; },
    setCaravan(next, nextState) { if (tier === next && state === nextState) return; tier = next; state = nextState; if (scene.body) scene.layout(); },
    pause(value) { paused = value; reduce(); },
    destroy() { resize.disconnect(); observer.disconnect(); media.removeEventListener("change", reduce); document.removeEventListener("visibilitychange", wake); game.destroy(true); canvas.remove(); delete parent.dataset.ready; },
  };
}
